import { useContext, useEffect, useRef, useState } from "react";
import { useLoanCalculatorWithPrincipalAmount } from "./useLoanCalculatorWithPrincipalAmount";
import {
  MIN_LOAN_AMOUNT,
  getMinDownPaymentValueByAssetAmount,
} from "../utils/downPaymentCalculation";
import { usePricings } from "../../../loanApplication/SummaryAndAdjust/shared/hooks/usePricings";
import { AssetType } from "@nemuru-eng/js-api-client";
import { useActionCallback } from "../../../../../utils/hooks/useActionCallback";
import { getMaxTermByAutoRegistrationDate } from "../utils/getMaxTermByAutoRegistrationDate";
import Text from "../../../../shared/components/Text";
import React from "react";
import { AuthContext } from "../../../../../contexts/AuthContext";
import { ProcessContext } from "../../../../../contexts/ProcessContext";

export const FACILITEA = "facilitea";

export const useDownPayment = ({ loan, dispatch }) => {
  const {
    loanConditions,
    setLoanConditionsByTerm,
    getLoanCalculatorByPrincipalAmount,
    patchSetLoanCalculator,
    setLoanConditions,
    termMarks,
    minTerm,
    maxTerm: maxTermByLoanCalculator,
  } = useLoanCalculatorWithPrincipalAmount();

  const {
    state: { agent },
  } = useContext(AuthContext);

  const {
    state: { merchant, asset },
  } = useContext(ProcessContext);

  const isFaciliteaAgent =
    (agent?.username && agent.username.toLowerCase().includes(FACILITEA)) ||
    merchant?.registration_fields.business_name
      .toLowerCase()
      .includes(FACILITEA);

  const { pricingByLoanId, requestGetPricingByLoanId } = usePricings();

  useEffect(() => {
    if (loan && loan.asset_reference[0].type !== AssetType.AUTO) {
      dispatch({
        type: "SET_FLOW_ITEM_OK",
      });
    }
  }, [loan]);

  const autoPrice = loan.asset_details.total_budget.amount;

  const [disableDownPayment, setDisableDownPayment] = useState(false);

  const [selectedDownPayment, setSelectedDownPayment] = useState({
    value:
      autoPrice - loan.loan_conditions.principal.amount <
      getMinDownPaymentValueByAssetAmount(autoPrice)
        ? getMinDownPaymentValueByAssetAmount(autoPrice)
        : autoPrice - loan.loan_conditions.principal.amount,
    invalidNumber: false,
  });

  const [autoAdjustedTerm, setAutoAdjustedTerm] = useState(undefined);
  const [loadingConditions, setLoadingConditions] = useState(false);

  const previousQuota = useRef(loanConditions.quota);

  const autoRegistrationDate = loan.asset_details.auto_registration_date;
  const isPreviousQuotaDifferent =
    previousQuota.current !== loanConditions.quota;

  const allowedMaxTerm = getMaxTermByAutoRegistrationDate(
    autoRegistrationDate,
    maxTermByLoanCalculator,
    isFaciliteaAgent
  );

  const filteredTermMarks = termMarks?.filter(
    (mark) => mark.value <= allowedMaxTerm
  );

  const [selectedTerm, setSelectedTerm] = useState({
    value: loanConditions.period,
    invalidNumber: false,
    invalidValue: false,
  });

  const [setupFeePercentage, setSetupFeePercentage] = useState(
    loanConditions.setupFee / loanConditions.principalAmount
  );

  useEffect(() => {
    requestGetPricingByLoanId();
    requestGetAutoAssetDetails();
  }, []);

  useEffect(() => {
    if (asset) {
      getLoanCalculatorByPrincipalAmount(principal);
    }
  }, [asset]);

  useEffect(() => {
    //TODO => temporary condition to disable down payment for facilitea agents
    if (isFaciliteaAgent) {
      setDisableDownPayment(true);
    }
  }, [agent]);

  useEffect(() => {
    if (loanConditions) {
      setSetupFeePercentage(
        loanConditions.setupFee / loanConditions.principalAmount
      );
    }
    if (termMarks && selectedTerm.value > allowedMaxTerm) {
      getSliderTermAndSetLoanConditions("", allowedMaxTerm);
      setAutoAdjustedTerm(allowedMaxTerm);
    }
  }, [loanConditions]);

  useEffect(() => {
    if (!pricingByLoanId && !asset) return;
    document
      .getElementById("downPaymentInputField")
      ?.addEventListener("keyup", (event) => {
        if (
          event.key === "Backspace" ||
          event.key === "Delete" ||
          event.key.match(/[0-9]/)
        ) {
          handleChangeCommitted();
        }
      });
  }, [pricingByLoanId]);

  useEffect(() => {
    if (
      loanConditions.assetAmount - loanConditions.principalAmount !==
        selectedDownPayment.value &&
      !isDownPaymentOutOfScope
    )
      setLoadingConditions(true);

    const timeout = setTimeout(() => {
      handleChangeCommitted();
    }, 300);

    return () => clearTimeout(timeout);
  }, [selectedDownPayment.value]);

  useEffect(() => {
    if (isPreviousQuotaDifferent) {
      previousQuota.current = loanConditions.quota;
      setLoadingConditions(false);
    }
  }, [loanConditions]);

  useActionCallback("REQUEST_PATCH_SET_LOAN_CALCULATOR_FULFILLED", () => {
    dispatch({
      type: "REQUEST_GET_LOAN",
      payload: loan.id,
    });
  });

  useActionCallback("REQUEST_GET_LOAN_FULFILLED", () => {
    dispatch({
      type: "SET_FLOW_ITEM_OK",
    });
  });

  const setTermStateRef = (data) => {
    termStateRef.current = data;
    setSelectedTerm({
      value: data,
      invalidNumber: isNaN(Number(data)),
      invalidValue: !filteredTermMarks.some(
        (termMark) => termMark.value === Number(data)
      ),
    });
  };

  const setDownPaymentStateRef = (_, data) => {
    downPaymentStateRef.current = Number(data);
    setSelectedDownPayment((s) => ({
      ...s,
      value: Number(data),
      invalidNumber: isNaN(Number(data)),
    }));
  };

  const getSliderTermAndSetLoanConditions = (_, term) => {
    setTermStateRef(term);
    setLoanConditionsByTerm(term);
  };

  const handleChangeCommitted = () => {
    const principal = autoPrice - downPaymentStateRef.current;
    if (
      downPaymentStateRef.current <
        getMinDownPaymentValueByAssetAmount(autoPrice) ||
      downPaymentStateRef.current > maxAutoDownPayment
    )
      return;
    setLoanConditions((s) => ({ ...s, principalAmount: principal }));
    getLoanCalculatorByPrincipalAmount(principal);
  };

  const requestGetAutoAssetDetails = () => {
    if (loan.asset_reference[0].type === AssetType.AUTO)
      dispatch({
        type: "REQUEST_GET_ASSET_DETAILS",
        payload: {
          assetId: loan.asset_reference[0].id,
          assetType: loan.asset_reference[0].type,
        },
      });
  };

  const onSubmitModule = () => {
    dispatch({
      type: "SET_FLOW_LOADING",
      payload: {
        show: true,
        loadingMessage: <Text tId="PLEASE_WAIT" />,
      },
    });
    const requestPatchSetLoanCalculator = patchSetLoanCalculator();
    if (requestPatchSetLoanCalculator !== true) {
      dispatch({
        type: "SET_FLOW_LOADING",
        payload: {
          show: false,
        },
      });
      dispatch({
        type: "SET_FLOW_ITEM_OK",
      });
    }
  };

  const minLoanAmount = pricingByLoanId?.range_principal_min
    ? pricingByLoanId.range_principal_min
    : MIN_LOAN_AMOUNT;
  const maxAutoDownPayment = autoPrice - minLoanAmount - 1;
  const termOutOfScope =
    selectedTerm.value < minTerm ||
    selectedTerm.value > maxTermByLoanCalculator;
  const downPaymentGreaterThanMax =
    selectedDownPayment.value > maxAutoDownPayment;
  const downPaymentLessThanMin =
    selectedDownPayment.value < getMinDownPaymentValueByAssetAmount(autoPrice);
  const isDownPaymentOutOfScope =
    downPaymentGreaterThanMax ||
    downPaymentLessThanMin ||
    selectedDownPayment.invalidNumber ||
    selectedTerm.invalidNumber;
  const totalAmountToPay = loanConditions.totalAmountToPay;
  const principal = autoPrice - selectedDownPayment.value;

  const termStateRef = useRef(selectedTerm.value);
  const downPaymentStateRef = useRef(selectedDownPayment.value);

  return {
    principal,
    loanConditions,
    onSubmitModule,
    totalAmountToPay,
    setDownPaymentStateRef,
    setTermStateRef,
    getSliderTermAndSetLoanConditions,
    handleChangeCommitted,
    setupFeePercentage,
    term: {
      selectedTerm,
      minTerm,
      termMarks: filteredTermMarks,
      allowedMaxTerm,
      termOutOfScope,
    },
    downPayment: {
      selectedDownPayment,
      maxAutoDownPayment,
      isDownPaymentOutOfScope,
      downPaymentGreaterThanMax,
      downPaymentLessThanMin,
    },
    autoAdjustedTerm,
    disableDownPayment,
    isFaciliteaAgent,
    loading: loadingConditions,
  };
};
